import React, { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/rates", label: "Rates" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <header className="glass-nav fixed top-0 left-0 right-0 z-40 h-20 flex items-center" data-testid="main-navbar">
      <div className="max-w-7xl w-full mx-auto px-6 sm:px-10 lg:px-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <div className="w-10 h-10 rounded-full border border-pink-hot/60 flex items-center justify-center glow-pink group-hover:animate-pink-pulse">
            <span className="font-display italic text-pink-hot text-lg leading-none">L</span>
          </div>
          <div className="hidden sm:block">
            <div className="font-display text-xl tracking-wide text-champagne">Layla</div>
            <div className="font-body text-[10px] tracking-[0.28em] text-pink-blush/80 -mt-1">LUXE · PINK · NOIR</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `pink-underline-link font-body text-xs tracking-[0.28em] uppercase ${
                  isActive ? "text-pink-hot" : "text-champagne/90 hover:text-pink-blush"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          {user && user.role === "admin" && (
            <Link
              to="/admin"
              className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-rosegold hover:text-pink-hot transition-colors"
              data-testid="admin-nav-link"
            >
              <Shield className="w-4 h-4" /> Admin
            </Link>
          )}
          {user && user.role !== "admin" && (
            <Link
              to="/account"
              className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-champagne/80 hover:text-pink-blush"
              data-testid="account-nav-link"
            >
              <User className="w-4 h-4" /> Account
            </Link>
          )}
          {user ? (
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-champagne/70 hover:text-pink-hot"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          ) : (
            <Link
              to="/login"
              className="px-5 py-2 text-xs tracking-[0.28em] uppercase text-champagne border border-pink-hot/50 hover:bg-pink-hot/10 hover:border-pink-hot rounded-sm transition-all"
              data-testid="login-nav-btn"
            >
              Sign In
            </Link>
          )}
          <Link
            to="/contact"
            className="px-6 py-2.5 text-xs tracking-[0.28em] uppercase bg-pink-hot text-white hover:bg-pink-glow rounded-sm transition-all glow-pink"
            data-testid="book-now-nav-btn"
          >
            Book Now
          </Link>
        </div>

        <button
          className="lg:hidden text-champagne"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden absolute top-20 left-0 right-0 glass-nav border-t border-pink-hot/20 py-6" data-testid="mobile-nav-panel">
          <div className="flex flex-col gap-4 px-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `font-body text-sm tracking-[0.28em] uppercase py-2 ${
                    isActive ? "text-pink-hot" : "text-champagne/90"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="divider-gold my-3" />
            {user && user.role === "admin" && (
              <Link to="/admin" onClick={() => setOpen(false)} className="text-sm tracking-[0.2em] uppercase text-rosegold py-2">Admin</Link>
            )}
            {user && user.role !== "admin" && (
              <Link to="/account" onClick={() => setOpen(false)} className="text-sm tracking-[0.2em] uppercase text-champagne/80 py-2">Account</Link>
            )}
            {user ? (
              <button onClick={() => { setOpen(false); handleLogout(); }} className="text-sm tracking-[0.2em] uppercase text-champagne/80 py-2 text-left">Logout</button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm tracking-[0.2em] uppercase text-champagne py-2">Sign In</Link>
            )}
            <Link to="/contact" onClick={() => setOpen(false)} className="mt-2 px-6 py-3 text-xs tracking-[0.28em] uppercase bg-pink-hot text-white text-center rounded-sm">Book Now</Link>
          </div>
        </div>
      )}
    </header>
  );
}
